import React, { useId } from 'react';
import {
  SPLIT_CASE_CONDITION_FULL,
  SPLIT_CASE_CONDITION_PARTIAL,
} from '_utils/constant';
import { useSplitCasePurchase } from '../../hooks';
import './style.scss';
import HelpInformation from '../HelpInformation';

export default function SplitcasePurchase({
  values: externalValues,
  onChange,
  onReset,
  onDelete,
  namePrefix,
  itemPrice,
  error,
}) {
  const autoId = useId();
  const radioGroupName = namePrefix || `sc-radio-${autoId}`;

  const {
    values: currentValues,
    isHidden,
    toggleHidden,
    handleFieldChange,
    handleReset,
  } = useSplitCasePurchase({ values: externalValues, itemPrice, onChange, onReset });

  const isPartial =
    currentValues.fulfilmentOption === 'incomplete' ||
    currentValues.fulfillCondition === SPLIT_CASE_CONDITION_PARTIAL;

  return (
    <div className="option-panel">
      <div className="option-panel-header">
        <div className="option-panel-title">
          Split Case Purchase
          <HelpInformation content="Sell this product in cases or cartons instead of single units. Buyers can order less than a full case, and it fills up as more people order it. Choose whether to fulfil an order only once its case is completely full, or go ahead even if it isn't. Turning this on also switches this product to Pay Later automatically."/>
        </div>
        <div className="option-panel-actions">
          <span
            className="btn-action-text"
            onClick={handleReset}
            role="button"
            tabIndex={0}
          >
            Reset
          </span>
          <span
            className="btn-action-text btn-action-text--highlight"
            onClick={toggleHidden}
            role="button"
            tabIndex={0}
          >
            {isHidden ? 'Show' : 'Hide'}
          </span>
          {onDelete && (
            <div
              className="icon-btn danger"
              title="Delete"
              onClick={onDelete}
              role="button"
              tabIndex={0}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
            </div>
          )}
        </div>
      </div>

      {!isHidden && (
        <div className="option-body">
          <div className="option-field">
            <span>Items per case/carton</span>
            <input
              type="number"
              value={currentValues.itemsPerCase ?? ''}
              placeholder="0"
              onChange={(e) => handleFieldChange('itemsPerCase', e.target.value)}
            />
          </div>
          <div className="option-field">
            <span>Cost per item</span>
            <input
              type="number"
              value={currentValues.costPerItem ?? ''}
              placeholder="0"
              onChange={(e) => handleFieldChange('costPerItem', e.target.value)}
            />
          </div>
          <div className="option-field">
            <span>Minimum item order</span>
            <input
              type="number"
              value={currentValues.minimumItemOrder ?? '1'}
              placeholder="1"
              onChange={(e) => handleFieldChange('minimumItemOrder', e.target.value)}
            />
          </div>
          <label className="option-radio">
            <input
              type="radio"
              name={radioGroupName}
              checked={
                currentValues.fulfilmentOption === 'full' ||
                currentValues.fulfillCondition === SPLIT_CASE_CONDITION_FULL
              }
              onChange={() =>
                handleFieldChange({
                  fulfilmentOption: 'full',
                  fulfillCondition: SPLIT_CASE_CONDITION_FULL,
                })
              }
            />
            Fulfil only when a full case is ordered
          </label>
          <label className="option-radio">
            <input
              type="radio"
              name={radioGroupName}
              checked={isPartial}
              onChange={() =>
                handleFieldChange({
                  fulfilmentOption: 'incomplete',
                  fulfillCondition: SPLIT_CASE_CONDITION_PARTIAL,
                  acknowledgePayLater: false,
                })
              }
            />
            Fulfil even if the case is incomplete
          </label>
          <label className={`option-ack ${isPartial ? 'option-ack--disabled' : ''}`}>
            <input
              type="checkbox"
              disabled={isPartial}
              checked={!isPartial && !!currentValues.acknowledgePayLater}
              onChange={(e) => handleFieldChange('acknowledgePayLater', e.target.checked)}
            />
            I acknowledge that enabling split case will automatically activate the Pay Later option.
          </label>
          {!isPartial && error && (
            <span style={{ color: '#E24B4A', fontSize: '11px', marginTop: '4px', display: 'block', fontWeight: 600 }}>
              {error}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
